import React, { memo } from 'react';

export type ViewMode = 'tiles' | 'list';

export interface ViewToggleSwitchProps {
    view: ViewMode;
    onChange: (view: ViewMode) => void;
}

export const ViewToggleSwitch: React.FC<ViewToggleSwitchProps> = memo(function ViewToggleSwitch({
    view,
    onChange,
}) {
    const handleToggle = () => {
        onChange(view === 'tiles' ? 'list' : 'tiles');
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleToggle();
        }
    };

    return (
        <div
            className="w-14 h-7 relative cursor-pointer"
            onClick={handleToggle}
            onKeyDown={handleKeyDown}
            role="switch"
            aria-checked={view === 'list'}
            aria-label="Toggle view"
            tabIndex={0}
        >
            {/* Border */}
            <div className="w-14 h-7 absolute left-0 top-0 rounded-2xl border-[1.50px] border-balloon" />
            
            {/* Toggle Button (Circle) */}
            <div
                className={`size-7 absolute top-0 bg-balloon rounded-full border border-balloon transition-all duration-200 ease-in-out ${
                    view === 'tiles' ? 'left-0' : 'left-[28px]'
                }`}
            />
            
            {/* Tiles Icon (Grid - left side) */}
            <div className="absolute left-[7px] top-[7px] pointer-events-none">
                <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className={view === 'tiles' ? 'text-white' : 'text-balloon'}
                >
                    <rect
                        x="0"
                        y="0"
                        width="6"
                        height="6"
                        fill="currentColor"
                        className="transition-colors duration-200"
                    />
                    <rect
                        x="8"
                        y="0"
                        width="6"
                        height="6"
                        fill="currentColor"
                        className="transition-colors duration-200"
                    />
                    <rect
                        x="0"
                        y="8"
                        width="6"
                        height="6"
                        fill="currentColor"
                        className="transition-colors duration-200"
                    />
                    <rect
                        x="8"
                        y="8"
                        width="6"
                        height="6"
                        fill="currentColor"
                        className="transition-colors duration-200"
                    />
                </svg>
            </div>
            
            {/* List Icon (Lines - right side) */}
            <div className="absolute left-[35px] top-[8px] pointer-events-none">
                <svg
                    width="14"
                    height="12"
                    viewBox="0 0 14 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className={view === 'list' ? 'text-white' : 'text-balloon'}
                >
                    <rect
                        x="0"
                        y="0"
                        width="14"
                        height="2"
                        rx="1"
                        fill="currentColor"
                        className="transition-colors duration-200"
                    />
                    <rect
                        x="0"
                        y="5"
                        width="14"
                        height="2"
                        rx="1"
                        fill="currentColor"
                        className="transition-colors duration-200"
                    />
                    <rect
                        x="0"
                        y="10"
                        width="14"
                        height="2"
                        rx="1"
                        fill="currentColor"
                        className="transition-colors duration-200"
                    />
                </svg>
            </div>
        </div>
    );
});
